import { ChevronRight, Menu, PanelLeft, Search } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface BreadcrumbSegment {
  label: string
  href?: string
}

export interface TopbarProps {
  /** Breadcrumb trail. Last segment is the current page (not a link). */
  breadcrumb?: BreadcrumbSegment[]
  /** Right-aligned action slot — Refresh, CARLA, user avatar, etc. */
  actions?: React.ReactNode
  /** Called when the centered command-palette trigger is clicked. */
  onOpenCommandPalette?: () => void
  /** Called when the sidebar-expand affordance is clicked. Only renders
   *  when the sidebar is collapsed — the shell wires this to `Cmd+B`. */
  onExpandSidebar?: () => void
  /** Called when the hamburger is tapped. Only provided (and rendered,
   *  `lg:hidden`) below the shell's 1024px breakpoint — the shell wires
   *  this to open the off-canvas `MobileSidebarDrawer`. */
  onOpenMobileNav?: () => void
  className?: string
}

/**
 * Fixed 48px top bar — every page inherits. Three regions:
 *   left: breadcrumb (+ expand-sidebar affordance when collapsed)
 *   center: command palette trigger with ⌘K hint
 *   right: action slot (Refresh, CARLA, user avatar)
 */
export function Topbar({
  breadcrumb = [],
  actions,
  onOpenCommandPalette,
  onExpandSidebar,
  onOpenMobileNav,
  className,
}: TopbarProps) {
  return (
    <header
      className={cn(
        'sticky top-0 z-sticky flex h-[var(--layout-topbar-height)] items-center gap-3 border-b border-border-subtle bg-surface px-3',
        className,
      )}
    >
      {onOpenMobileNav ? (
        <button
          type="button"
          aria-label="Open navigation"
          onClick={onOpenMobileNav}
          className="inline-flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary lg:hidden"
        >
          <Menu size={16} />
        </button>
      ) : null}

      {onExpandSidebar ? (
        <button
          type="button"
          aria-label="Expand sidebar"
          onClick={onExpandSidebar}
          className="inline-flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <PanelLeft size={14} />
        </button>
      ) : null}

      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 overflow-hidden text-sm">
        {breadcrumb.map((seg, idx) => {
          const isLast = idx === breadcrumb.length - 1
          return (
            <span key={`${seg.label}-${idx}`} className="flex min-w-0 items-center gap-1.5">
              {idx > 0 ? (
                <ChevronRight size={12} className="flex-shrink-0 text-tertiary" />
              ) : null}
              {isLast || !seg.href ? (
                <span
                  className={cn('truncate', isLast ? 'text-primary font-medium' : 'text-secondary')}
                >
                  {seg.label}
                </span>
              ) : (
                <a
                  href={seg.href}
                  className="truncate text-secondary no-underline hover:text-primary hover:underline"
                >
                  {seg.label}
                </a>
              )}
            </span>
          )
        })}
      </nav>

      <button
        type="button"
        onClick={onOpenCommandPalette}
        aria-label="Open command palette"
        className={cn(
          'mx-auto flex h-7 flex-1 cursor-pointer items-center gap-2 rounded-sm border border-border-default bg-surface-sunk px-2.5 text-sm text-tertiary',
          'lg:min-w-[280px] lg:max-w-md lg:flex-none',
          'hover:border-border-strong hover:text-secondary',
        )}
      >
        <Search size={12} className="flex-shrink-0 text-tertiary" />
        <span className="hidden flex-1 truncate text-left sm:inline">
          Search orders, customers, products…
        </span>
        <span
          className="text-role-kbd-shortcut hidden items-center rounded-xs px-1 lg:inline-flex"
          style={{ background: 'var(--color-kbd-bg)' }}
        >
          ⌘K
        </span>
      </button>

      {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
    </header>
  )
}
